
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { mockGigs } from './mockGigData';
import GigCard from './GigCard';
import { Link } from 'react-router-dom';
import { ExternalLink, Sparkles } from 'lucide-react';

const GigCalendar: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  
  // Convert gig dates to Date objects for comparison
  const gigDates = mockGigs.map(gig => new Date(gig.date).toDateString());
  
  // Get gigs for the selected date
  const selectedGigs = mockGigs.filter(
    gig => new Date(gig.date).toDateString() === selectedDate?.toDateString()
  );
  
  // Find dates that have gigs for highlighting in the calendar
  const hasGigOnDate = (date: Date) => {
    return gigDates.includes(date.toDateString());
  };
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Performance Calendar</h2>
        <Link 
          to="/calendar-scheduling" 
          className="text-sm text-primary hover:underline flex items-center gap-1 bg-primary/10 px-3 py-1.5 rounded-full transition-colors hover:bg-primary/20"
        >
          <Sparkles className="h-3.5 w-3.5" />
          Use AI Calendar <ExternalLink className="h-3 w-3 ml-1" />
        </Link>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="col-span-1">
          <CardContent className="p-4">
            <CalendarComponent
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              className="w-full"
              modifiers={{
                hasGig: (date) => hasGigOnDate(date),
              }}
              modifiersStyles={{
                hasGig: { 
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(147, 51, 234, 0.1)',
                  borderRadius: '0',
                }
              }}
              components={{
                DayContent: (props) => {
                  // We need to extract the date from props
                  const { date } = props;
                  const hasGig = date ? hasGigOnDate(date) : false;
                  
                  return (
                    <div className="relative w-full h-full flex items-center justify-center">
                      <div>{date ? date.getDate() : ''}</div>
                      {hasGig && (
                        <div className="absolute bottom-1 left-1/2 -translate-x-1/2">
                          <div className="h-1 w-1 rounded-full bg-primary"></div>
                        </div>
                      )}
                    </div>
                  );
                }
              }}
            />
          </CardContent>
        </Card>
        
        <Card className="col-span-1 lg:col-span-2">
          <CardContent className="p-4">
            <h3 className="text-lg font-semibold mb-4">
              {selectedDate ? (
                <>
                  Gigs on{' '}
                  <span className="text-primary">
                    {selectedDate.toLocaleDateString('en-US', { 
                      month: 'long', 
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                </>
              ) : 'Select a date'}
            </h3>
            
            {selectedGigs.length > 0 ? (
              <div className="space-y-4 divide-y divide-border">
                {selectedGigs.map(gig => (
                  <GigCard key={gig.id} gig={gig} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No gigs scheduled for this date.</p>
                <Badge variant="outline" className="mt-2 cursor-pointer">+ Add Gig</Badge>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default GigCalendar;
